// SuggestedPostFeed.jsx
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Observer from '@researchgate/react-intersection-observer';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import UniversalContainer from '../component/UniversalContainer.jsx';
import Loading from '../component/Loading.jsx';

import objectifyArrayById from '../resource/objectifyArrayById.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';

import media from '../style/media.js';

export const SuggestedPostFeed = ({
  meId = null,
  feedName = null,
  nextPage = null,
  prefetchedMessageAmount = 2,
  isFirstPageFetching = false,
  isNextPageFetching = false,
  currentAutoplayPostId = '',
  postIds = EMPTY_ARRAY,
  fetchFeeds = NULL_FUNCTION,
  setMessageHeightInFeed = NULL_FUNCTION,
}) => {
  const [isIntersected, setIsIntersected] = useState(false);
  const resizeObserverRef = useRef(null);
  const currentAutoplayPostIndex = postIds.indexOf(currentAutoplayPostId);
  useEffect(() => {
    if (isIntersected) {
      resizeObserverRef.current = new ResizeObserver(entries => {
        const messageObject = objectifyArrayById({
          array: entries.map(entry => ({
            id: entry.target.getAttribute('data-message_id'),
            messageHeight: entry.target.clientHeight,
          })),
        });
        return setMessageHeightInFeed({
          feedName,
          messageObject,
        });
      });
    }
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [feedName, isIntersected, setMessageHeightInFeed]);
  useEffect(() => {
    if (feedName && isIntersected && !postIds.length && !isFirstPageFetching) {
      fetchFeeds({
        page: 1,
        type: feedName,
      });
    }
  }, [meId, feedName, isIntersected, isFirstPageFetching, postIds, fetchFeeds]);
  return (
    <Observer
      rootMargin={'150px'}
      onChange={({ isIntersecting }, unobserve) => {
        if (isIntersecting) {
          setIsIntersected(true);
          unobserve();
        }
      }}
    >
      <StyledSuggestedPostFeed>
        <InfiniteScroller
          useWindow
          pageStart={1}
          hasMore={nextPage != null}
          loadMore={() =>
            fetchFeeds({
              page: nextPage,
              type: feedName,
            })
          }
          isFetching={isFirstPageFetching || isNextPageFetching}
        >
          <Posts>
            <ScrollItem
              loader={
                <PostWrapper>
                  <Loading size={24} />
                </PostWrapper>
              }
            >
              {postIds.map((messageId, index) => {
                return (
                  <PostWrapper
                    key={index}
                    data-message_id={messageId}
                    ref={element => {
                      if (element && resizeObserverRef.current) {
                        resizeObserverRef.current.unobserve(element);
                        resizeObserverRef.current.observe(element);
                      }
                    }}
                  >
                    <UniversalContainer
                      filename="Post"
                      messageId={messageId}
                      shouldPrefetch={
                        // If there is any post has been intersected (currentAutoplayPostIndex >= 0),
                        // prefetch the segments for intersected one and the next two (if any)
                        currentAutoplayPostIndex >= 0 &&
                        index >= currentAutoplayPostIndex &&
                        index <=
                          currentAutoplayPostIndex + prefetchedMessageAmount
                      }
                      shouldShowSubscribeAndUnlock
                      messageHeightFeedName={feedName}
                    />
                  </PostWrapper>
                );
              })}
            </ScrollItem>
          </Posts>
        </InfiniteScroller>
      </StyledSuggestedPostFeed>
    </Observer>
  );
};

SuggestedPostFeed.propTypes = {
  meId: PropTypes.string,
  feedName: PropTypes.string,
  currentAutoplayPostId: PropTypes.string,
  postIds: PropTypes.array,
  nextPage: PropTypes.number,
  prefetchedMessageAmount: PropTypes.number,
  isFirstPageFetching: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  fetchFeeds: PropTypes.func,
  setMessageHeightInFeed: PropTypes.func,
};

const StyledSuggestedPostFeed = styled.div``;

const Posts = styled.div``;

const PostWrapper = styled.div`
  margin-bottom: 74px;
  ${media.mobile`
    margin-bottom: 32px;
  `};
`;

export default SuggestedPostFeed;
